import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, KeyRound, Copy, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetData, setResetData] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setResetData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const resetLink = resetData
    ? `${window.location.origin}/reset-password?token=${resetData.reset_token}`
    : '';

  const copyLink = () => {
    navigator.clipboard.writeText(resetLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-heading font-bold mb-2" data-testid="forgot-password-title">
            Reset Password
          </h1>
          <p className="text-textSecondary">
            Enter your email address and we'll generate a reset link
          </p>
        </div>

        <div className="glass p-8 rounded-sm">
          {!resetData ? (
            <>
              {error && (
                <div className="bg-red-500/20 border border-red-500 text-red-500 px-4 py-3 rounded-sm mb-6" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="forgot-email-input"
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-white/30"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                  className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase py-3 transition-all duration-300 red-glow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Get Reset Link'}
                </button>
              </form>
            </>
          ) : (
            <div className="space-y-4" data-testid="reset-link-section">
              <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-sm text-sm">
                {resetData.message}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-textSecondary">Reset Link (expires in {resetData.expires_in})</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={resetLink}
                    readOnly
                    data-testid="reset-link-display"
                    className="flex-1 bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-3 text-sm font-mono truncate"
                  />
                  <button
                    onClick={copyLink}
                    data-testid="copy-reset-link"
                    className="bg-surfaceHighlight hover:bg-white/10 border border-white/10 text-white rounded-sm px-4 py-3 transition-colors"
                  >
                    {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Link
                to={`/reset-password?token=${resetData.reset_token}`}
                data-testid="go-to-reset-link"
                className="block w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase py-3 text-center transition-all duration-300 red-glow"
              >
                Reset Password Now
              </Link>

              <button
                onClick={() => { setResetData(null); setEmail(''); }}
                className="w-full text-textSecondary hover:text-white text-sm text-center transition-colors"
              >
                Request another reset
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-primary hover:underline font-medium flex items-center justify-center space-x-2" data-testid="back-to-login">
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Login</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
